// src/WaitlistForm.tsx

import React, { useState } from 'react';
import Modal from './Modal';
import PrivacyPolicy from './PrivacyPolicy';

interface FormData {
  fullName: string;
  email: string;
  jobTitle: string;
  companyName: string;
  additionalInfo: string;
}

// Define the API response interface to ensure type safety
interface ApiResponse {
  success: boolean;
  message?: string;
  error?: string;
  details?: {
    email?: string | null;
    fullName?: string | null;
  };
}

const initialFormState: FormData = {
  fullName: '',
  email: '',
  jobTitle: '',
  companyName: '',
  additionalInfo: ''
};

// Get the API URL based on the environment
const API_URL = import.meta.env.DEV 
  ? 'http://localhost:8787'
  : 'https://api.toposphere.net';

const WaitlistForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [hasConsented, setHasConsented] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formError) setFormError(null);
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasConsented(e.target.checked);
    if (formError && formError.includes('privacy policy')) {
      setFormError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate consent
    if (!hasConsented) {
      setFormError('Please accept the privacy policy to continue.');
      return;
    }

    // Validate required fields
    const requiredFields: (keyof FormData)[] = ['fullName', 'email', 'jobTitle', 'companyName'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());
    
    if (missingFields.length > 0) {
      setFormError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    try {
      setIsSubmitting(true);
      setFormError(null);
      
      // Make the API call to the worker
      const response = await fetch(`${API_URL}/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit form');
      }

      if (!data.success) {
        // Handle validation errors from the API
        if (data.details) {
          const errorMessages = Object.entries(data.details)
            .filter(([_, value]) => value !== null)
            .map(([field, message]) => message)
            .join(', ');
          throw new Error(errorMessages || data.message || 'Validation failed');
        }
        throw new Error(data.message || 'Something went wrong');
      }

      // Reset form on success
      setFormData(initialFormState);
      setHasConsented(false);
      setIsSubmitted(true);
    } catch (error) {
      // Type guard to ensure error is an Error object
      if (error instanceof Error) {
        setFormError(error.message);
      } else {
        setFormError('An unexpected error occurred. Please try again.');
      }
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Thank you message component
  const ThankYouMessage = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] w-full">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/20 mb-6">
        <svg 
          className="w-8 h-8 text-blue-400" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <div className="space-y-6 w-full max-w-2xl">
        <h2 className="text-3xl font-bold text-white text-center">
          Thank You for Joining!
        </h2>
        <p className="text-lg text-white/80 text-center">
          We're working hard to create something amazing and appreciate your interest. 
          You will be among the first to know about the launch!
        </p>
      </div>
    </div>
  );

  return (
    <section id="waitlist" className="py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-[#0a1425] to-[#050614] rounded-xl p-8 border border-white/10 shadow-2xl">
          {!isSubmitted ? (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 text-white">
                  Join the Waitlist
                </h2>
                <p className="text-sm text-white/80 max-w-2xl mx-auto">
                  While we're working on making topoSphere generally available, we invite you to join our waitlist. Share your visualization needs and be the first to know when we launch. 
                  Your insights will help shape the future of topoSphere.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Form fields */}
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="fullName" className="block text-sm font-medium text-white">
                        Full Name <span className="text-blue-400">*</span>
                      </label>
                      <input
                        id="fullName"
                        type="text"
                        name="fullName"
                        required
                        value={formData.fullName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white
                                 placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                                 transition-colors"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-medium text-white">
                        Work Email <span className="text-blue-400">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white
                                 placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                                 transition-colors"
                        placeholder="Enter your work email"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="jobTitle" className="block text-sm font-medium text-white">
                        Job Title <span className="text-blue-400">*</span>
                      </label>
                      <input
                        id="jobTitle"
                        type="text"
                        name="jobTitle"
                        required
                        value={formData.jobTitle}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white
                                 placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                                 transition-colors"
                        placeholder="Enter your job title"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="companyName" className="block text-sm font-medium text-white">
                        Organization Name <span className="text-blue-400">*</span>
                      </label>
                      <input
                        id="companyName"
                        type="text"
                        name="companyName"
                        required
                        value={formData.companyName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white
                                 placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                                 transition-colors"
                        placeholder="Enter your organization name"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-2">
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-white">
                    What do you hope to achieve with topoSphere?
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white
                             placeholder-white/40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                             transition-colors h-32 resize-none"
                    placeholder="Tell us about your specific visualization needs or share any feedback"
                  />
                </div>

                {/* Privacy Policy Consent */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="privacy-consent"
                    checked={hasConsented}
                    onChange={handleConsentChange}
                    className="mt-1 h-4 w-4 rounded border-white/10 bg-white/5 text-blue-500
                             focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-[#0a1425]"
                  />
                  <label htmlFor="privacy-consent" className="text-sm text-white/60">
                    I agree to the{' '}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setIsPrivacyModalOpen(true);
                      }}
                      className="text-blue-400 hover:text-blue-300 underline"
                      type="button"
                    >
                      privacy policy
                    </button>
                    {' '}and consent to receiving important updates about topoSphere's launch 
                    and features. We respect your inbox and promise not to spam – you'll 
                    only hear from us with meaningful updates.
                  </label>
                </div>

                {/* Error Message Display */}
                {formError && (
                  <div className="text-red-400 text-sm text-center" role="alert">
                    {formError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !hasConsented}
                  className={`w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 
                            rounded-lg text-lg font-semibold transition-all shadow-lg 
                            focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-[#0a1425]
                            ${(isSubmitting || !hasConsented) 
                              ? 'opacity-50 cursor-not-allowed from-blue-400 to-blue-500' 
                              : 'hover:from-blue-600 hover:to-blue-700'}`}
                >
                  {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
                </button>

                {/* Privacy Note */}
                <p className="text-xs text-white/60 text-center">
                  We're committed to protecting your data. View our full{' '}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPrivacyModalOpen(true);
                    }}
                    className="text-blue-400 hover:text-blue-300 underline"
                    type="button"
                  >
                    privacy policy
                  </button>
                  {' '}for details on how we process and protect your information.
                </p>
              </form>
            </>
          ) : (
            <ThankYouMessage />
          )}
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <Modal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title="Privacy Policy"
      >
        <PrivacyPolicy />
      </Modal>
    </section>
  );
};

export default WaitlistForm;
